// 评分工具函数：统一处理不同版本的维度命名

// 标准维度映射（version 2.0）
const STANDARD_DIMENSIONS = {
  technical_security: {
    label: 'Technical Security',
    labelZh: '技术安全',
    weight: 35,
    description: 'Audit quality, formal verification, access control, bug bounty, historical incidents'
  },
  economic_health: {
    label: 'Economic Health',
    labelZh: '经济健康',
    weight: 30,
    description: 'Liquidity quality, stress tests, oracle architecture, TVL composition'
  },
  governance_operations: {
    label: 'Governance & Operations',
    labelZh: '治理与运营',
    weight: 20,
    description: 'Decentralization, bus factor, governance health, process quality'
  },
  tokenomics_valuation: {
    label: 'Tokenomics & Valuation',
    labelZh: '代币经济学与估值',
    weight: 15,
    description: 'Value capture, inflation model, valuation ratios'
  }
};

// 旧版本维度映射（version 1.0 或其他变体）
const LEGACY_DIMENSION_MAP = {
  // 旧版本可能的命名
  security: 'technical_security',
  technical_security: 'technical_security', // 保持
  smart_contract_security: 'technical_security',
  
  liquidity: 'economic_health',
  economic_health: 'economic_health', // 保持
  financial: 'economic_health',
  
  code_quality: 'technical_security', // 可能属于技术安全
  decentralization: 'governance_operations',
  governance_operations: 'governance_operations', // 保持
  governance: 'governance_operations',
  
  centralization: 'governance_operations',
  tokenomics_valuation: 'tokenomics_valuation', // 保持
  tokenomics: 'tokenomics_valuation',
  valuation: 'tokenomics_valuation',
  
  technology_infrastructure: 'technical_security',
};

/**
 * 标准化维度名称
 * @param {string} dimensionKey - 原始维度键名
 * @returns {string} 标准化的维度键名
 */
export function normalizeDimensionKey(dimensionKey) {
  if (!dimensionKey || typeof dimensionKey !== 'string') {
    return null;
  }
  
  const normalized = dimensionKey.toLowerCase().trim();
  
  // 如果已经是标准格式，直接返回
  if (STANDARD_DIMENSIONS[normalized]) {
    return normalized;
  }
  
  // 查找映射
  const mapped = LEGACY_DIMENSION_MAP[normalized];
  if (mapped) {
    return mapped;
  }
  
  // 模糊匹配（处理下划线和连字符变体）
  const fuzzyMatch = Object.keys(LEGACY_DIMENSION_MAP).find(key => {
    const keyNormalized = key.toLowerCase().replace(/[_-]/g, '');
    const inputNormalized = normalized.replace(/[_-]/g, '');
    return keyNormalized === inputNormalized;
  });
  
  if (fuzzyMatch) {
    return LEGACY_DIMENSION_MAP[fuzzyMatch];
  }
  
  // 如果找不到映射，返回原值（让调用者处理）
  return normalized;
}

/**
 * 标准化整个 subscores 对象
 * @param {Object} subscores - 原始 subscores 对象
 * @returns {Object} 标准化后的 subscores 对象
 */
export function normalizeSubscores(subscores) {
  if (!subscores || typeof subscores !== 'object') {
    return {};
  }
  
  const normalized = {};
  
  for (const [key, value] of Object.entries(subscores)) {
    const standardKey = normalizeDimensionKey(key);
    if (standardKey) {
      // 如果已存在该标准键，合并（保留更高的权重）
      if (normalized[standardKey]) {
        // 如果新值的权重更高，使用新值
        const existingWeight = normalized[standardKey].weight || 0;
        const newWeight = value?.weight || 0;
        if (newWeight > existingWeight) {
          normalized[standardKey] = {
            ...value,
            weight: newWeight || STANDARD_DIMENSIONS[standardKey]?.weight || 0
          };
        }
      } else {
        normalized[standardKey] = {
          ...value,
          weight: value?.weight || STANDARD_DIMENSIONS[standardKey]?.weight || 0
        };
      }
    }
  }
  
  return normalized;
}

/**
 * 获取维度的显示名称
 * @param {string} dimensionKey - 维度键名
 * @param {string} lang - 语言 ('en' | 'zh')
 * @returns {string} 显示名称
 */
export function getDimensionLabel(dimensionKey, lang = 'en') {
  const standardKey = normalizeDimensionKey(dimensionKey);
  if (!standardKey || !STANDARD_DIMENSIONS[standardKey]) {
    // 如果找不到标准映射，返回格式化的原键名
    return dimensionKey
      .split(/[_-]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
  
  return lang === 'zh' 
    ? STANDARD_DIMENSIONS[standardKey].labelZh
    : STANDARD_DIMENSIONS[standardKey].label;
}

/**
 * 获取维度的描述
 * @param {string} dimensionKey - 维度键名
 * @returns {string} 描述
 */
export function getDimensionDescription(dimensionKey) {
  const standardKey = normalizeDimensionKey(dimensionKey);
  if (!standardKey || !STANDARD_DIMENSIONS[standardKey]) {
    return '';
  }
  
  return STANDARD_DIMENSIONS[standardKey].description;
}

/**
 * 按权重排序维度
 * @param {Object} subscores - subscores 对象
 * @returns {Array} 排序后的 [key, value] 数组
 */
export function sortSubscoresByWeight(subscores) {
  const normalized = normalizeSubscores(subscores);
  
  return Object.entries(normalized).sort((a, b) => {
    const weightA = a[1]?.weight || 0;
    const weightB = b[1]?.weight || 0;
    return weightB - weightA; // 降序
  });
}

